<template>
  <div class="dca-history" data-testid="dca-allocation-history">
    <div class="text-body-2 font-weight-bold mb-2">Change history</div>

    <SoSkeleton v-if="loading" height="48" data-testid="dca-history-skeleton" />

    <!--
      A failed load must not read as an empty history. "No changes recorded" is a
      claim about the data; saying it when the request failed is simply untrue, and
      indistinguishable from the truth for anyone reading it.
    -->
    <p
      v-else-if="error"
      class="text-caption text-error"
      data-testid="dca-history-error"
    >
      Could not load the change history for this step.
    </p>

    <p
      v-else-if="changes.length === 0"
      class="text-caption text-medium-emphasis"
      data-testid="dca-history-empty"
    >
      No changes recorded for this step yet.
    </p>

    <ol v-else class="dca-history__list">
      <li
        v-for="change in changes"
        :key="change.changeId"
        class="dca-history__item"
        :data-testid="`dca-history-change-${change.changeId}`"
      >
        <div class="text-caption text-medium-emphasis">
          {{ who(change) }} · {{ when(change) }}
        </div>
        <ul class="dca-history__moves">
          <li
            v-for="entry in moved(change)"
            :key="entry.dca"
            class="text-caption"
          >
            {{ describe(entry) }}
          </li>
        </ul>
      </li>
    </ol>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import {
    IDcaAllocationChange,
    IDcaAllocationHistoryEntry,
  } from "@/models/dcaAllocation/dcaAllocation.types"
  import { agencyName } from "../dcaAgencyColours"

  /**
   * What each save to this cell replaced.
   *
   * The config table is overwritten in place, so without this a change leaves no trace
   * beyond who touched the row last. Moving volume between agencies is a commercial
   * commitment, so "what did this replace, and who decided it" has to survive the save.
   */
  export default defineComponent({
    components: { SoSkeleton },
    props: {
      changes: {
        type: Array as PropType<IDcaAllocationChange[]>,
        default: () => [],
      },
      loading: { type: Boolean, default: false },
      error: { type: Boolean, default: false },
    },
    methods: {
      /** The seeded starting state has no author, and should not claim one. */
      who(change: IDcaAllocationChange): string {
        return change.changedBy ?? "Initial configuration"
      },
      when(change: IDcaAllocationChange): string {
        const at = new Date(change.changedAt)
        return Number.isNaN(at.getTime())
          ? change.changedAt
          : new Intl.DateTimeFormat("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }).format(at)
      },
      /**
       * A save records every agency in the cell, including ones it left alone, so that
       * each change is readable on its own. The list only shows what actually moved.
       */
      moved(change: IDcaAllocationChange): IDcaAllocationHistoryEntry[] {
        return change.entries.filter(
          (entry) => entry.previousPercentage !== entry.newPercentage,
        )
      },
      describe(entry: IDcaAllocationHistoryEntry): string {
        const name = agencyName(entry.dca)
        // null is "not in the split", which is not the same as 0% - an agency held at 0
        // is retired but still present, and can be re-enabled without being re-added.
        if (entry.previousPercentage === null) {
          return `${name} added at ${entry.newPercentage}%`
        }
        if (entry.newPercentage === null) {
          return `${name} removed, was ${entry.previousPercentage}%`
        }
        return `${name} ${entry.previousPercentage}% → ${entry.newPercentage}%`
      },
    },
  })
</script>

<style scoped>
  .dca-history__list,
  .dca-history__moves {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  .dca-history__item + .dca-history__item {
    padding-top: 8px;
    margin-top: 8px;
    border-top: 1px solid rgb(var(--v-theme-border));
  }
</style>
